import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertTriangle, TrendingUp, Target, Lightbulb, Check } from 'lucide-react';

export default function BrandRefinement() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Brand Refinement Recommendations
        </h1>
        <p className="text-base text-body leading-relaxed">
          Strategic recommendations to strengthen brand implementation, enhance digital presence,
          and maximise the impact of our refined brand system.
        </p>
      </div>

      <Card className="p-8 bg-surface">
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Current Brand Strengths
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-body mb-1">Refined Typography System</p>
              <p className="text-sm text-subtle">Bebas Neue and Montserrat create professional, modern hierarchy</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-body mb-1">Optimised Readability</p>
              <p className="text-sm text-subtle">#565656 body text ensures excellent legibility across all platforms</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-body mb-1">Consistent Logo Usage</p>
              <p className="text-sm text-subtle">Clear guidelines with actual logo assets for all applications</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-body mb-1">Professional Color System</p>
              <p className="text-sm text-subtle">Jackstone Blue paired with neutral grays creates trust and clarity</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-body mb-1">Strong Heritage Identity</p>
              <p className="text-sm text-subtle">85+ years of excellence clearly communicated</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-body mb-1">Comprehensive Guidelines</p>
              <p className="text-sm text-subtle">Complete documentation for all brand touchpoints</p>
            </div>
          </div>
        </div>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Strategic Enhancement Opportunities
        </h2>
        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-accent-teal/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Target className="h-6 w-6 text-accent-teal" />
              </div>
              <div className="flex-1">
                <Badge tone="green" fill="soft" className="mb-2">Quick Win</Badge>
                <h3 className="text-lg font-heading font-semibold text-jackstone-blue mb-2">
                  Digital Brand Asset Library
                </h3>
                <p className="text-sm text-body mb-3">
                  Create a centralized digital repository for all brand assets including logo files,
                  font packages, color swatches, and approved templates. This ensures teams always
                  have access to the latest, approved materials.
                </p>
                <div className="bg-surface p-3 rounded text-xs">
                  <p className="font-medium text-body mb-1">Action Items:</p>
                  <ul className="text-subtle space-y-1">
                    <li>• Set up cloud-based brand portal (Frontify, Brandfolder, or similar)</li>
                    <li>• Upload all logo variations (SVG, PNG, EPS formats)</li>
                    <li>• Include downloadable font packages with licenses</li>
                    <li>• Add templates for PowerPoint, Word, email signatures</li>
                    <li>• Create quick-reference one-pagers for common use cases</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-jackstone-blue/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-jackstone-blue" />
              </div>
              <div className="flex-1">
                <Badge tone="teal" fill="soft" className="mb-2">Medium-Term</Badge>
                <h3 className="text-lg font-heading font-semibold text-jackstone-blue mb-2">
                  Website Brand Alignment
                </h3>
                <p className="text-sm text-body mb-3">
                  Update the Jackstone.com website to fully reflect the refined brand system.
                  Current site typography and colors should be updated to match these guidelines
                  for complete brand consistency.
                </p>
                <div className="bg-surface p-3 rounded text-xs">
                  <p className="font-medium text-body mb-1">Recommendations:</p>
                  <ul className="text-subtle space-y-1">
                    <li>• Implement Bebas Neue for all major headlines</li>
                    <li>• Apply Montserrat Semibold/Medium throughout</li>
                    <li>• Update body text to #565656 for optimal readability</li>
                    <li>• Ensure H1 headings use Jackstone Blue consistently</li>
                    <li>• Add subtle animations for professional polish</li>
                    <li>• Optimize mobile typography and spacing</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-accent-red/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Lightbulb className="h-6 w-6 text-accent-red" />
              </div>
              <div className="flex-1">
                <Badge tone="green" fill="soft" className="mb-2">Quick Win</Badge>
                <h3 className="text-lg font-heading font-semibold text-jackstone-blue mb-2">
                  Marketing Template Suite
                </h3>
                <p className="text-sm text-body mb-3">
                  Develop pre-designed templates that incorporate the new brand guidelines,
                  making it easy for team members to create on-brand materials without
                  design expertise.
                </p>
                <div className="bg-surface p-3 rounded text-xs">
                  <p className="font-medium text-body mb-1">Priority Templates:</p>
                  <ul className="text-subtle space-y-1">
                    <li>• PowerPoint presentation deck (branded master slides)</li>
                    <li>• Sales proposal template with proper typography</li>
                    <li>• Email signature templates (HTML format)</li>
                    <li>• Social media post templates (LinkedIn, Facebook)</li>
                    <li>• Brochure layouts using brand fonts and colors</li>
                    <li>• Case study template with consistent styling</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-light-blue/40 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-body" />
              </div>
              <div className="flex-1">
                <Badge tone="teal" fill="soft" className="mb-2">Medium-Term</Badge>
                <h3 className="text-lg font-heading font-semibold text-jackstone-blue mb-2">
                  Brand Training & Rollout
                </h3>
                <p className="text-sm text-body mb-3">
                  Conduct comprehensive training sessions to ensure all team members, partners,
                  and agencies understand and can properly implement the brand guidelines.
                </p>
                <div className="bg-surface p-3 rounded text-xs">
                  <p className="font-medium text-body mb-1">Training Program:</p>
                  <ul className="text-subtle space-y-1">
                    <li>• Host brand guidelines presentation for all departments</li>
                    <li>• Create video tutorials for common design tasks</li>
                    <li>• Develop quick reference cards for typography and color usage</li>
                    <li>• Establish brand champions in each department</li>
                    <li>• Schedule quarterly brand consistency reviews</li>
                    <li>• Provide agency partners with complete brand package</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-light-blue rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-jackstone-blue" />
              </div>
              <div className="flex-1">
                <Badge tone="purple" fill="soft" className="mb-2">Strategic</Badge>
                <h3 className="text-lg font-heading font-semibold text-jackstone-blue mb-2">
                  Enhanced Digital Presence
                </h3>
                <p className="text-sm text-body mb-3">
                  Leverage the refined brand system to strengthen digital marketing efforts
                  and establish Jackstone as a thought leader in industrial freezing technology.
                </p>
                <div className="bg-surface p-3 rounded text-xs">
                  <p className="font-medium text-body mb-1">Long-term Strategy:</p>
                  <ul className="text-subtle space-y-1">
                    <li>• Develop content marketing strategy showcasing technical expertise</li>
                    <li>• Create technical white papers with branded templates</li>
                    <li>• Produce customer case study videos with consistent branding</li>
                    <li>• Enhance LinkedIn presence with professional branded content</li>
                    <li>• Develop interactive product configurator with brand styling</li>
                    <li>• Launch technical blog with consistent visual identity</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-accent-teal/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <Target className="h-6 w-6 text-accent-teal" />
              </div>
              <div className="flex-1">
                <Badge tone="green" fill="soft" className="mb-2">Quick Win</Badge>
                <h3 className="text-lg font-heading font-semibold text-jackstone-blue mb-2">
                  Print Collateral Update
                </h3>
                <p className="text-sm text-body mb-3">
                  Refresh existing print materials to align with the new brand guidelines.
                  Priority items include business cards, brochures, and trade show materials.
                </p>
                <div className="bg-surface p-3 rounded text-xs">
                  <p className="font-medium text-body mb-1">Priority Updates:</p>
                  <ul className="text-subtle space-y-1">
                    <li>• Redesign business cards with correct typography</li>
                    <li>• Update product brochures using new font system</li>
                    <li>• Refresh trade show booth graphics and banners</li>
                    <li>• Create new letterhead and envelope templates</li>
                    <li>• Update presentation folders and leave-behinds</li>
                    <li>• Reprint frequently-used sales materials first</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6 bg-light-blue border-jackstone-blue/20">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Implementation Roadmap
        </h3>
        <div className="space-y-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Badge tone="green" fill="soft">Phase 1: Immediate (1-2 months)</Badge>
            </div>
            <ul className="text-sm text-body space-y-1 ml-4">
              <li>• Set up digital brand asset library</li>
              <li>• Create marketing template suite</li>
              <li>• Update business cards and core print materials</li>
              <li>• Begin team training on brand guidelines</li>
              <li>• Audit current materials for inconsistencies</li>
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Badge tone="teal" fill="soft">Phase 2: Near-term (3-6 months)</Badge>
            </div>
            <ul className="text-sm text-body space-y-1 ml-4">
              <li>• Complete website brand alignment</li>
              <li>• Roll out brand training across all departments</li>
              <li>• Refresh all print collateral inventory</li>
              <li>• Update social media profiles and templates</li>
              <li>• Establish quarterly brand review process</li>
            </ul>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Badge tone="purple" fill="soft">Phase 3: Long-term (6-12 months)</Badge>
            </div>
            <ul className="text-sm text-body space-y-1 ml-4">
              <li>• Launch enhanced digital marketing initiatives</li>
              <li>• Develop comprehensive content library</li>
              <li>• Measure brand recognition and consistency</li>
              <li>• Expand brand guidelines based on new applications</li>
              <li>• Continue refinement and optimization</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-3">
          Measuring Success
        </h3>
        <p className="text-body mb-4">
          To ensure successful brand implementation and ongoing consistency:
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <h4 className="font-medium text-body">Internal Metrics</h4>
            <ul className="text-sm text-subtle space-y-1">
              <li>• Brand asset library usage and downloads</li>
              <li>• Template adoption rates across teams</li>
              <li>• Brand consistency scores in quarterly audits</li>
              <li>• Training completion rates</li>
              <li>• Time saved using branded templates</li>
            </ul>
          </div>
          <div className="space-y-2">
            <h4 className="font-medium text-body">External Metrics</h4>
            <ul className="text-sm text-subtle space-y-1">
              <li>• Brand recognition in target markets</li>
              <li>• Website engagement and time on site</li>
              <li>• Social media engagement rates</li>
              <li>• Lead quality from marketing materials</li>
              <li>• Customer feedback on brand perception</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-surface">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-3">
          Maintaining Brand Excellence
        </h3>
        <p className="text-body mb-4">
          Ongoing brand management ensures long-term consistency and impact:
        </p>
        <div className="space-y-2 text-sm text-body">
          <p>• Designate a brand manager or champion to oversee implementation</p>
          <p>• Establish approval workflow for all external-facing materials</p>
          <p>• Conduct quarterly brand audits across all channels</p>
          <p>• Update brand guidelines annually based on evolving needs</p>
          <p>• Share brand wins and case studies to reinforce importance</p>
          <p>• Maintain open communication channel for brand questions</p>
          <p>• Review and refresh templates every 6 months</p>
        </div>
      </Card>
    </div>
  );
}
